import React, { useMemo } from 'react';
import { Trash2 } from 'lucide-react';
import { TableActionButton } from '../../../components/ui';
import toast from 'react-hot-toast';

export const SelectedItemsTable = ({ selectedItems, handleItemChange, removeItem }) => {
  if (!selectedItems || selectedItems.length === 0) return null;

  return (
    <div className="col-span-2 mt-2 space-y-4">
      <label className="text-slate-700 font-bold text-[12px] tracking-wide capitalize flex items-center gap-2">
        Selected Items 
        <span className="bg-brand-dark text-white rounded-full text-[10px] w-5 h-5 flex items-center justify-center">
          {selectedItems.length}
        </span>
      </label>
      
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-left border-collapse whitespace-nowrap">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-[13px] font-bold text-slate-700 uppercase tracking-wider">
              <th className="px-2 py-1.5 text-center w-12 border-r border-slate-200">S.no</th>
              <th className="px-2 py-1.5 border-r border-slate-200">Vendor</th>
              <th className="px-2 py-1.5 border-r border-slate-200">Category</th>
              <th className="px-2 py-1.5 border-r border-slate-200">Sub Category</th>
              <th className="px-2 py-1.5 border-r border-slate-200">Article Id</th>
              <th className="px-2 py-1.5 border-r border-slate-200 min-w-[200px]">Item Description</th>
              <th className="px-2 py-1.5 border-r border-slate-200 min-w-[200px]">Information</th>
              <th className="px-2 py-1.5 text-center w-24 min-w-[70px] border-r border-slate-200">Avail Stock</th>
              <th className="px-2 py-1.5 text-center w-24 min-w-[70px] border-r border-slate-200">Qty</th>
              <th className="px-2 py-1.5 text-right w-24 min-w-[90px] border-r border-slate-200">Purchase Amount</th>
              <th className="px-2 py-1.5 text-right w-32 min-w-[100px] border-r border-slate-200">Price</th>
              <th className="px-2 py-1.5 text-right w-24 min-w-[80px] border-r border-slate-200">Total</th>
              <th className="px-2 py-1.5 text-center w-24 min-w-[70px] border-r border-slate-200">Dis%</th>
              <th className="px-2 py-1.5 text-right w-24 min-w-[80px] border-r border-slate-200">Dis Amt</th>
              <th className="px-2 py-1.5 text-right w-24 min-w-[80px] border-r border-slate-200">Amount</th>
              <th className="px-2 py-1.5 text-center w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-[13px]">
            {selectedItems.map((si, idx) => {
              const available = Number(si.item.availableQty ?? si.item.stock ?? si.item.quantity ?? 0);
              const qty = Number(si.qty || 0);
              const price = Number(String(si.price || 0).replace(/,/g, ''));
              const disPercent = Number(si.disPercent || 0);
              
              const isOverdraft = qty > available;
              
              const total = qty * price;
              const disAmt = total * (disPercent / 100);
              const amount = total - disAmt;

              return (
                <tr key={idx} className={`transition-all ${isOverdraft ? 'bg-red-50/50' : 'bg-white hover:bg-slate-50/50'}`}>
                  <td className="px-2 py-1.5 text-center border-r border-slate-100 font-medium text-slate-500">
                    {idx + 1}
                  </td>
                  <td className="px-2 py-1.5 border-r border-slate-100 font-medium text-slate-800">
                    {si.item.vendorName || si.item.vendor || si.item.supplier || '—'}
                  </td>
                  <td className="px-2 py-1.5 border-r border-slate-100 font-medium text-slate-800">
                    {si.item.category || '—'}
                  </td>
                  <td className="px-2 py-1.5 border-r border-slate-100 font-medium text-slate-800">
                    {si.item.subCategory || '—'}
                  </td>
                  <td className="px-2 py-1.5 border-r border-slate-100 font-medium text-slate-800">
                    {si.item.articleId || si.item.itemCode || 'N/A'}
                  </td>
                  <td className="px-2 py-1.5 border-r border-slate-100 whitespace-normal">
                    <div className="font-bold text-slate-800 leading-tight">
                      {si.item.itemName || si.item.description || 'Unknown'}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 border-r border-slate-100 whitespace-normal">
                    <div className="text-slate-600 leading-tight">
                      {si.item.information || si.item.remark || '—'}
                    </div>
                    {isOverdraft && (
                      <div className="text-[10px] text-red-500 font-bold mt-1">
                        Only {available} available
                      </div>
                    )}
                  </td>
                  {/* Available Stock */}
                  <td className="px-2 py-1.5 text-center border-r border-slate-100 font-bold text-brand-dark">
                    {available} <span className="text-[10px] text-slate-500 font-medium ml-0.5">{si.item?.uom || si.item?.unit || 'Set'}</span>
                  </td>
                  
                  {/* Qty Input */}
                  <td className="px-2 py-1.5 text-center border-r border-slate-100">
                    <input 
                      type="number" 
                      max={available}
                      className={`w-full text-center text-[12px] font-bold border rounded-md py-1.5 px-2 transition-all outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield] ${
                        isOverdraft 
                          ? 'border-red-500 text-red-600 bg-red-50 focus:ring-1 focus:ring-red-500' 
                          : 'border-slate-200 focus:border-brand-dark focus:ring-1 focus:ring-brand-dark'
                      }`}
                      value={si.qty} 
                      onChange={(e) => {
                        let val = e.target.value;
                        if (Number(val) > available) {
                          val = available.toString();
                          toast.error(`Only ${available} count is available`);
                        }
                        handleItemChange(idx, 'qty', val);
                      }} 
                    />
                  </td>

                  {/* Purchase Amount */}
                  <td className="px-2 py-1.5 text-right border-r border-slate-100 font-medium text-slate-700">
                    {si.item.blumPrice && !isNaN(Number(String(si.item.blumPrice).replace(/,/g, ''))) 
                      ? Number(String(si.item.blumPrice).replace(/,/g, '')).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) 
                      : '—'}
                  </td>
                  
                  {/* Price Input */}
                  <td className="px-2 py-1.5 text-right border-r border-slate-100">
                    <input 
                      type="text" 
                      className="w-full text-right text-[12px] font-bold border border-slate-200 rounded-md py-1.5 px-2 outline-none focus:border-brand-dark focus:ring-1 focus:ring-brand-dark transition-all" 
                      value={si.price} 
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.]/g, '');
                        handleItemChange(idx, 'price', val);
                      }}
                      onBlur={(e) => {
                        const val = Number(e.target.value.replace(/,/g, ''));
                        if (!isNaN(val) && e.target.value !== '') {
                          handleItemChange(idx, 'price', val.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2}));
                        }
                      }}
                      onFocus={(e) => {
                        const val = String(e.target.value).replace(/,/g, '');
                        handleItemChange(idx, 'price', val);
                      }}
                    />
                  </td>
                  
                  {/* Total */}
                  <td className="px-2 py-1.5 text-right border-r border-slate-100 font-semibold text-slate-700">
                    {total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  
                  {/* Dis% Input */}
                  <td className="px-2 py-1.5 text-center border-r border-slate-100">
                    <input 
                      type="number" 
                      className="w-full text-center text-[12px] font-bold border border-slate-200 rounded-md py-1.5 px-2 outline-none focus:border-brand-dark focus:ring-1 focus:ring-brand-dark transition-all [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]" 
                      value={si.disPercent !== undefined ? si.disPercent : 0} 
                      onChange={(e) => handleItemChange(idx, 'disPercent', e.target.value)} 
                    />
                  </td>
                  
                  {/* Dis Amt */}
                  <td className="px-2 py-1.5 text-right border-r border-slate-100 font-medium text-red-500">
                    {disAmt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  
                  {/* Amount */}
                  <td className="px-2 py-1.5 text-right border-r border-slate-100 font-bold text-brand-dark">
                    {amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  
                  {/* Actions */}
                  <td className="p-3">
                    <div className="flex items-center justify-center">
                      <TableActionButton icon={Trash2} variant="danger" onClick={() => removeItem(idx)} title="Remove Item" />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
